import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const WaveCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, t = 0;
    const resize = () => {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      t += 0.004;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const w = canvas.width, h = canvas.height;
      for (let line = 0; line < 4; line++) {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 8) {
          const y =
            h * 0.55 +
            Math.sin(x * 0.0016 + t * (1 + line * 0.35) + line * 1.7) * 60 +
            Math.sin(x * 0.004 - t * 1.4 + line) * 26 +
            line * 42;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = line === 1 ? "rgba(4,120,87,0.28)" : `rgba(15,23,42,${0.1 - line * 0.018})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const HeroSection = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#FAF9F6] pt-[72px]">
      <motion.div style={{ y: yBg }} className="absolute inset-0 opacity-80">
        <WaveCanvas />
      </motion.div>
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/70 mb-10"
          data-testid="hero-regulation-badge"
        >
          <ShieldCheck size={14} className="text-emerald-700" />
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-800">{t.hero.badge}</span>
        </motion.div>

        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-[5.2rem] font-bold tracking-tight text-slate-900 leading-[1.06] max-w-4xl" data-testid="hero-title">
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                {i === 2 ? <em className="not-italic text-emerald-800">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl"
          data-testid="hero-lead"
        >
          {t.hero.lead}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button data-testid="hero-cta-markets" onClick={() => go("markets")} className="px-8 py-4 bg-slate-900 text-white rounded-full text-sm font-medium hover:bg-emerald-800 transition-colors duration-300">
            {t.hero.cta1}
          </button>
          <button data-testid="hero-cta-regulation" onClick={() => go("regulation")} className="px-8 py-4 border border-slate-300 text-slate-900 rounded-full text-sm font-medium hover:border-slate-900 transition-colors duration-300 inline-flex items-center gap-2">
            {t.hero.cta2} <ArrowDown size={15} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-slate-200"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} className={`py-6 pr-6 ${i > 0 ? "lg:border-l lg:border-slate-200 lg:pl-6" : ""}`}>
              <div className="font-serif text-3xl lg:text-4xl font-bold text-slate-900">{s.value}</div>
              <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-slate-500">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
